import OutputController from "./output-controller";
import {
  IAttribute,
  IDictionary,
  IEdges,
  IImageMeta,
  ILabelOptionValue,
  IOption,
  ISizeControllerState,
} from "../../typings";
import TemplateController from "./template-controller";
import Cropper from "cropperjs";
import UnitSystemController, { UnitSystem } from "./unit-system-controller";
import { dpi300Files } from "../configuration";
import reduceClosestNumber from "../helpers/reduceClosestNumber";
import { Ratios, ratios } from "./size-controller/ratios";

interface IOutputAttributes {
  width: string[];
  height: string[];
  croppedData: string[];
  edges: string[];
  edgesIncludes: string[];
  edge: string[];
}

const reversedRatiosIndexes: number[] = [
  0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7,
  0.75, 0.8, 0.85, 0.9, 0.95, 1, 1.05, 1.11, 1.18, 1.25, 1.33, 1.43, 1.54, 1.67,
  1.82, 2, 2.22, 2.5, 2.86, 3.33, 4, 5, 6.67, 10, 20,
];

interface IRatio extends ISize {
  amount: number;
  maxWidth?: number;
  maxHeight?: number;
}

export default class SizeController {
  static METRIC_EDGE_SIZE: number = 6;
  static IMPERIAL_EDGE_SIZE: number = 6.35;
  public test: boolean = false;
  private outputController: OutputController;
  private templateController: TemplateController;
  private unitSystemController: UnitSystemController;
  private size: ISize;
  private ratio: IRatio = { amount: 1, width: 70, height: 70 };
  private scale: number;
  private maxSize?: ISize;
  private maxValues: { small: number; large: number } = {
    small: 200,
    large: 400,
  };
  private minimumScaleValue: number;
  private maxSizeValue: number;
  private outputAttributes: IOutputAttributes;
  private children: IDictionary<SizeController> = {};

  constructor(
    outputController: OutputController,
    templateController: TemplateController,
    outputAttributes: IOutputAttributes,
    unitSystemController: UnitSystemController,
  ) {
    this.outputController = outputController;
    this.templateController = templateController;
    this.outputAttributes = outputAttributes;
    this.unitSystemController = unitSystemController;

    const attributes: IDictionary<IAttribute> =
      this.templateController.getTemplate()?.attributes;
    const metric: number = attributes?.minScaleValueMetric?.value;
    const imperial: number = attributes?.minScaleValueImperial?.value;
    this.minimumScaleValue =
      this.unitSystemController.getUnitSystem() === UnitSystem.METRIC
        ? metric
          ? metric
          : 8
        : imperial
          ? imperial
          : 7.62;
  }

  get edgeSize(): number {
    if (typeof this.unitSystemController.getUnitSystem() !== "string") {
      throw (
        this.unitSystemController.getUnitSystem() + "No unit system specified!"
      );
    }

    const edge: string = this.outputController.getValue(
      this.outputAttributes.edge,
    );

    return (
      (edge === "sewing-allowance-edge-fold" ? 2 : 1) *
      (this.unitSystemController.getUnitSystem() === UnitSystem.METRIC
        ? SizeController.METRIC_EDGE_SIZE
        : SizeController.IMPERIAL_EDGE_SIZE)
    );
  }

  private getRatio(): IRatio {
    const cropperData: Cropper.Data = this.getCropperData();

    if (cropperData === null) {
      const ratio: IRatio = this.getCurrentRatios()[1];
      return {
        ...ratio,
      };
    }

    const ratioAmount: number = cropperData.width / cropperData.height;

    const roundedRatioAmount: number =
      SizeController.roundAndSetDecimal(ratioAmount);

    const index: number = reversedRatiosIndexes.reduce<number>(
      (previousValue: number, currentValue: number) => {
        if (roundedRatioAmount >= currentValue) {
          return currentValue;
        }
        return previousValue;
      },
      reversedRatiosIndexes[0],
    );

    const { width, height, maxWidth, maxHeight }: IRatio =
      this.getCurrentRatios()[index];

    if (maxWidth) {
      return {
        amount: ratioAmount,
        width: SizeController.roundAndSetDecimal(height * ratioAmount),
        height,
        maxWidth,
      };
    }

    return {
      amount: ratioAmount,
      width,
      height: SizeController.roundAndSetDecimal(width / ratioAmount),
      maxHeight,
    };
  }

  public async setScale(scale: number): Promise<void> {
    const fixedSize: number = this.getFixedStepByScale(scale);
    const ratio: IRatio = this.getRatio();

    if (fixedSize) {
      return this.setInnerSizeByFixedSize(fixedSize);
    }

    if (ratio.maxWidth && ratio.amount > 1) {
      const width: number = SizeController.roundAndSetDecimal(
        (ratio.maxWidth -
          this.minimumScaleValue * ratio.amount -
          this.getHorizontalEdgeSize()) *
          scale +
          this.minimumScaleValue * ratio.amount,
      );
      const height: number = SizeController.roundAndSetDecimal(
        width / ratio.amount,
      );
      return await this.setInnerSize(width, height);
    }

    if (ratio.maxWidth && ratio.amount <= 1) {
      const width: number = SizeController.roundAndSetDecimal(
        (ratio.maxWidth -
          this.minimumScaleValue -
          this.getHorizontalEdgeSize()) *
          scale +
          this.minimumScaleValue,
      );
      const height: number = SizeController.roundAndSetDecimal(
        width / ratio.amount,
      );
      return await this.setInnerSize(width, height);
    }

    if (ratio.maxHeight && ratio.amount >= 1) {
      const height: number = SizeController.roundAndSetDecimal(
        (ratio.maxHeight -
          this.minimumScaleValue -
          this.getVerticalEdgeSize()) *
          scale +
          this.minimumScaleValue,
      );
      const width: number = SizeController.roundAndSetDecimal(
        height * ratio.amount,
      );
      return await this.setInnerSize(width, height);
    }

    if (ratio.maxHeight && ratio.amount < 1) {
      const height: number = SizeController.roundAndSetDecimal(
        (ratio.maxHeight -
          this.minimumScaleValue / ratio.amount -
          this.getVerticalEdgeSize()) *
          scale +
          this.minimumScaleValue / ratio.amount,
      );
      const width: number = SizeController.roundAndSetDecimal(
        height * ratio.amount,
      );
      return await this.setInnerSize(width, height);
    }
  }

  public getScale(): number {
    const size: ISize = this.getInnerSize();
    const fixedSteps: number[] = this.getFixedSteps();

    if (fixedSteps) {
      const step: number =
        fixedSteps.indexOf(
          reduceClosestNumber(
            size.width <= size.height ? size.width : size.height,
            fixedSteps,
          ),
        ) + 1;
      return (1 / (fixedSteps.length - 1)) * step;
    }

    const ratio: IRatio = this.getRatio();

    if (ratio.maxWidth && ratio.amount > 1) {
      return (
        (1 /
          (ratio.maxWidth -
            this.minimumScaleValue * ratio.amount -
            this.getHorizontalEdgeSize())) *
        (size.width - this.minimumScaleValue * ratio.amount)
      );
    }

    if (ratio.maxWidth && ratio.amount <= 1) {
      return (
        (1 /
          (ratio.maxWidth -
            this.minimumScaleValue -
            this.getHorizontalEdgeSize())) *
        (size.width - this.minimumScaleValue)
      );
    }

    if (ratio.maxHeight && ratio.amount >= 1) {
      return (
        (1 /
          (ratio.maxHeight -
            this.minimumScaleValue -
            this.getVerticalEdgeSize())) *
        (size.height - this.minimumScaleValue)
      );
    }

    if (ratio.maxHeight && ratio.amount < 1) {
      return (
        (1 /
          (ratio.maxHeight -
            this.minimumScaleValue / ratio.amount -
            this.getVerticalEdgeSize())) *
        (size.height - this.minimumScaleValue / ratio.amount)
      );
    }
  }

  private async setInnerSize(
    innerWidth: number,
    innerHeight: number,
  ): Promise<void> {
    const width: number = innerWidth + this.getHorizontalEdgeSize();
    const height: number = innerHeight + this.getVerticalEdgeSize();

    if (this.outputController.getValue(this.outputAttributes.width) !== width) {
      await this.outputController.setValue(this.outputAttributes.width, width);
    }

    if (
      this.outputController.getValue(this.outputAttributes.height) !== height
    ) {
      await this.outputController.setValue(
        this.outputAttributes.height,
        height,
      );
    }

    if (this.getScale() > 1) {
      await this.setScale(1);
    }

    if (this.getScale() < 0) {
      await this.setScale(0);
    }
  }

  public getSize(): ISize {
    return {
      width: this.outputController.getValue(this.outputAttributes.width),
      height: this.outputController.getValue(this.outputAttributes.height),
    };
  }

  public getInnerSize(): ISize {
    const { width, height }: ISize = this.getSize();
    return {
      width: parseFloat((width - this.getHorizontalEdgeSize()).toPrecision(12)),
      height: parseFloat((height - this.getVerticalEdgeSize()).toPrecision(12)),
    };
  }

  public async increaseWidthBy(widthAdder: number): Promise<void> {
    const isMetric: boolean =
      this.unitSystemController.getUnitSystem() === UnitSystem.METRIC;
    widthAdder /= isMetric ? 10 : 3.9370078740157477;

    const size: ISize = this.getInnerSize();

    const width: number = size.width + widthAdder;
    const height: number = width * (size.height / size.width);

    await this.setInnerSize(width, height);
  }

  public async increaseHeightBy(heightAdder: number): Promise<void> {
    const isMetric: boolean =
      this.unitSystemController.getUnitSystem() === UnitSystem.METRIC;
    heightAdder /= isMetric ? 10 : 3.9370078740157477;

    const size: ISize = this.getInnerSize();

    const height: number = size.height + heightAdder;
    const width: number = height * (size.width / size.height);

    await this.setInnerSize(width, height);
  }

  static roundAndSetDecimal(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private getEdges(): IEdges {
    const edges: IEdges = this.outputController.getValue(
      this.outputAttributes.edges,
    );

    if (!edges) {
      return {};
    }

    return edges;
  }

  public async setEdgeSide(
    side: "top" | "right" | "bottom" | "left",
    value: boolean,
  ): Promise<void> {
    const innerSize: ISize = this.getInnerSize();
    const edges: IEdges = this.getEdges();
    const oldSize: ISize = this.getSize();

    if (edges[side] !== value) {
      await this.outputController.setValue(
        this.outputAttributes.edges,
        Object.assign({}, edges, { [side]: value }),
      );
    }

    const fixedSteps: number[] = this.getFixedSteps();
    if (fixedSteps) {
      const horizontalEdgeSize: number = this.getHorizontalEdgeSize();
      const verticalEdgeSize: number = this.getVerticalEdgeSize();
      const fixedSize: number = reduceClosestNumber(
        innerSize.width + horizontalEdgeSize <=
          innerSize.height + verticalEdgeSize
          ? innerSize.width + horizontalEdgeSize
          : innerSize.height + verticalEdgeSize,
        fixedSteps,
      );

      return this.setInnerSizeByFixedSize(fixedSize);
    }

    await this.setInnerSize(innerSize.width, innerSize.height);
  }

  public setInnerSizeByFixedSize(fixedSize: number): Promise<void> {
    const cropperData: Cropper.Data = this.getCropperData();
    const ratioAmount: number = cropperData.width / cropperData.height;

    const horizontalFixedWidth: number = fixedSize;
    const horizontalFixedHeight: number =
      (fixedSize - this.getHorizontalEdgeSize()) / ratioAmount +
      this.getVerticalEdgeSize();

    if (horizontalFixedWidth <= horizontalFixedHeight) {
      const width: number = fixedSize - this.getHorizontalEdgeSize();
      return this.setInnerSize(width, width / ratioAmount);
    }

    const height: number = fixedSize - this.getVerticalEdgeSize();
    return this.setInnerSize(height * ratioAmount, height);
  }

  public async setTopEdge(top: boolean): Promise<void> {
    await this.setEdgeSide("top", top);
  }

  public async setRightEdge(right: boolean): Promise<void> {
    await this.setEdgeSide("right", right);
  }

  public async setBottomEdge(bottom: boolean): Promise<void> {
    await this.setEdgeSide("bottom", bottom);
  }

  public async setLeftEdge(left: boolean): Promise<void> {
    await this.setEdgeSide("left", left);
  }

  public async setEdge(edge: string): Promise<void> {
    const innerSize: ISize = this.getInnerSize();
    await this.outputController.setValue(this.outputAttributes.edge, edge);
    await this.setInnerSize(innerSize.width, innerSize.height);

    if (edge === "sewing-allowance") {
      return;
    }

    const edges: IEdges = this.getEdges();
    this.outputController.startEventTransaction();
    if (edges.top || edges.bottom) {
      this.setTopEdge(edges.top);
      this.setRightEdge(false);
      this.setBottomEdge(edges.bottom);
      this.setLeftEdge(false);
    }
    if (edges.left || edges.right) {
      this.setTopEdge(false);
      this.setRightEdge(edges.right);
      this.setBottomEdge(false);
      this.setLeftEdge(edges.left);
    }
    if (edges.top && edges.bottom) {
      this.setTopEdge(true);
      this.setRightEdge(false);
      this.setBottomEdge(true);
      this.setLeftEdge(false);
    }
    if (edges.left && edges.right) {
      this.setTopEdge(false);
      this.setRightEdge(true);
      this.setBottomEdge(false);
      this.setLeftEdge(true);
    }
    this.outputController.commitEventTransaction();
  }

  public async newImageCroppedData(
    croppedData: Cropper.Data,
    meta: IImageMeta,
  ): Promise<void> {
    await this.outputController.setValue(
      this.outputAttributes.croppedData,
      croppedData,
    );

    const fixedSteps: number[] = this.getFixedSteps();

    if (dpi300Files.indexOf(meta.type) !== -1) {
      const width: number = croppedData.width / this.getDpiToMm();
      const height: number = croppedData.height / this.getDpiToMm();

      if (fixedSteps) {
        await this.setInnerSizeByFixedSteps(width, height, fixedSteps);
        return;
      }

      return await this.setInnerSize(width, height);
    }

    const { width, height }: IRatio = this.getRatio();

    if (fixedSteps) {
      await this.setInnerSizeByFixedSteps(width, height, fixedSteps);
      return;
    }

    await this.setInnerSize(width, height);
  }

  private getCropperData(): Cropper.Data {
    return this.outputController.getValue(this.outputAttributes.croppedData);
  }

  public async setCroppedData(newCroppedData: Cropper.Data): Promise<void> {
    await this.outputController.setValue(
      this.outputAttributes.croppedData,
      newCroppedData,
    );
    const innerSize: ISize = this.getInnerSize();

    const baseSize: number =
      innerSize.width > innerSize.height ? innerSize.width : innerSize.height;

    const ratio: number = newCroppedData.width / newCroppedData.height;
    if (ratio > 1) {
      return await this.setInnerSize(baseSize, baseSize / ratio);
    }

    await this.setInnerSize(baseSize * ratio, baseSize);
  }

  getEdgeSize(): number {
    return this.edgeSize;
  }

  public getState(): ISizeControllerState {
    const { width, height }: ISize = this.getSize();
    return JSON.parse(
      JSON.stringify({
        croppedData: this.outputController.getValue(
          this.outputAttributes.croppedData,
        ),
        edges: this.getEdges(),
        edgesIncluded: this.outputController.getValue(
          this.outputAttributes.edgesIncludes,
        ),
        width,
        height,
      }),
    );
  }

  public async restoreState({
    croppedData,
    edges,
    edgesIncluded,
    width,
    height,
  }: ISizeControllerState): Promise<void> {
    await this.outputController.setValue(
      this.outputAttributes.croppedData,
      croppedData,
    );
    await this.outputController.setValue(this.outputAttributes.edges, edges);
    await this.outputController.setValue(
      this.outputAttributes.edgesIncludes,
      edgesIncluded,
    );
    await this.outputController.setValue(this.outputAttributes.width, width);
    await this.outputController.setValue(this.outputAttributes.height, height);
  }

  private getFixedSteps(): number[] {
    const option: IOption =
      this.templateController.getOptionByCode("size-value");

    if (!option?.attributes?.fixed_steps) {
      return;
    }

    return option.attributes.fixed_steps.value;
  }

  private getFixedStepByScale(scale: number): number {
    const fixedSteps: number[] = this.getFixedSteps();

    if (!fixedSteps) {
      return;
    }

    const index: number = (fixedSteps.length - 1) * scale;
    return fixedSteps[Math.round(index)];
  }

  public getSteps(): number {
    const fixedSteps: number[] = this.getFixedSteps();

    if (fixedSteps) {
      return fixedSteps.length - 1;
    }

    const ratio: IRatio = this.getRatio();
    const isMetric: boolean =
      this.unitSystemController.getUnitSystem() === UnitSystem.METRIC;
    return (
      ((ratio.maxWidth ? ratio.maxWidth : ratio.maxHeight) -
        this.minimumScaleValue) /
      (isMetric ? 1 : 25.4) /
      (isMetric ? 1 : 0.05)
    );
  }

  public async setEdgesIncludes(edgesIncluded: boolean): Promise<void> {
    const { width, height }: ISize = this.getInnerSize();
    await this.outputController.setValue(
      this.outputAttributes.edgesIncludes,
      edgesIncluded,
    );
    await this.setInnerSize(width, height);
  }

  public setChild(index: string, sizeController: SizeController): void {
    this.children[index] = sizeController;
  }

  public getChild(index: string): SizeController {
    return this.children[index];
  }

  async setSize(width: number, height: number): Promise<void> {
    const fixedSteps: number[] = this.getFixedSteps();
    if (fixedSteps) {
      const size: number = width <= height ? width : height;
      const closest: number = reduceClosestNumber(size, fixedSteps);
      if (width <= height) {
        width = closest;
      } else {
        height = closest;
      }
    }

    if (width < this.minimumScaleValue) {
      return;
    }

    if (height < this.minimumScaleValue) {
      return;
    }

    const size: ISize = {
      ...this.getSize(),
    };

    const edgesIncludes: boolean = this.outputController.getValue(
      this.outputAttributes.edgesIncludes,
    );

    const edges: IEdges = this.outputController.getValue(
      this.outputAttributes.edges,
    );

    const edgeSize: number = this.getEdgeSize();
    if (!edgesIncludes && edges && edges.top) {
      size.height -= edgeSize;
      height -= edgeSize;
    }

    if (!edgesIncludes && edges && edges.right) {
      size.width -= edgeSize;
      width -= edgeSize;
    }

    if (!edgesIncludes && edges && edges.bottom) {
      size.height -= edgeSize;
      height -= edgeSize;
    }

    if (!edgesIncludes && edges && edges.left) {
      size.width -= edgeSize;
      width -= edgeSize;
    }

    const croppedData: Cropper.Data = this.outputController.getValue(
      this.outputAttributes.croppedData,
    );

    //Return when no image has been uploaded and croppedData isn't available yet
    if (croppedData === null) {
      return;
    }

    const modWidth: number = size.width / width;
    const modHeight: number = size.height / height;

    if (croppedData.width / modWidth < 1) {
      return;
    }

    this.outputController.startEventTransaction();
    await this.outputController.setValue(this.outputAttributes.croppedData, {
      ...croppedData,
      x: croppedData.x + (croppedData.width - croppedData.width / modWidth) / 2,
      y:
        croppedData.y +
        (croppedData.height - croppedData.height / modHeight) / 2,
      width: croppedData.width / modWidth,
      height: croppedData.height / modHeight,
    });

    this.setInnerSize(width, height);
    this.outputController.commitEventTransaction();
  }

  public generateCroppedData(meta: IImageMeta): Cropper.Data {
    const imageData: Cropper.Data = {
      width: meta.width,
      height: meta.height,
      x: 0,
      y: 0,
      rotate: 0,
      scaleX: 1,
      scaleY: 1,
    };

    const optionWidth: IOption =
      this.templateController.getOptionByCode("width");
    const fixedWidth: ILabelOptionValue = optionWidth.attributes.fixed
      ? optionWidth.attributes.fixed.value
      : undefined;
    const optionHeight: IOption =
      this.templateController.getOptionByCode("height");
    const fixedHeight: ILabelOptionValue = optionHeight.attributes.fixed
      ? optionHeight.attributes.fixed.value
      : undefined;
    if (
      typeof fixedWidth === "number" &&
      typeof fixedHeight === "number" &&
      typeof meta.width === "number" &&
      typeof meta.height === "number"
    ) {
      const ratioToHeight: number = fixedHeight / fixedWidth;
      const ratioToWidth: number = fixedWidth / fixedHeight;
      if (meta.width * ratioToHeight > meta.height) {
        imageData.width = ratioToWidth * meta.height;
        imageData.x = (meta.width - imageData.width) / 2;
      } else {
        imageData.height = ratioToHeight * meta.width;
        imageData.y = (meta.height - imageData.height) / 2;
      }
    }

    return imageData;
  }

  public async setUnitSystem(unitSystem: UnitSystem): Promise<void> {
    const innerSize: ISize = this.getInnerSize();
    await this.unitSystemController.saveUnitSystem(unitSystem);

    const fixedSteps: number[] = this.getFixedSteps();
    if (fixedSteps) {
      return this.setInnerSizeByFixedSteps(
        innerSize.width,
        innerSize.height,
        fixedSteps,
      );
    }

    await this.setInnerSize(innerSize.width, innerSize.height);
  }

  public getDpi(): number {
    return this.templateController.getOptionByCode("image").attributes.dpi
      .value;
  }

  // divide the pixels of the data with this value and you get the size according to the dpi back
  public getDpiToMm(): number {
    return 0.0393700787 * this.getDpi();
  }

  private setInnerSizeByFixedSteps(
    width: number,
    height: number,
    fixedSteps: number[],
  ): Promise<void> {
    const horizontalEdgeSize: number = this.getHorizontalEdgeSize();
    const verticalEdgeSize: number = this.getVerticalEdgeSize();
    const size: number =
      width + horizontalEdgeSize <= height + verticalEdgeSize
        ? width + horizontalEdgeSize
        : height + verticalEdgeSize;
    const fixedSize: number = reduceClosestNumber(size, fixedSteps);

    return this.setInnerSizeByFixedSize(fixedSize);
  }

  private getHorizontalEdgeSize(): number {
    const edgesIncludes: boolean = this.outputController.getValue(
      this.outputAttributes.edgesIncludes,
    );
    const edges: IEdges = this.getEdges();
    return (
      (!edgesIncludes && edges && edges.left ? this.edgeSize : 0) +
      (!edgesIncludes && edges && edges.right ? this.edgeSize : 0)
    );
  }

  private getVerticalEdgeSize(): number {
    const edgesIncludes: boolean = this.outputController.getValue(
      this.outputAttributes.edgesIncludes,
    );
    const edges: IEdges = this.getEdges();
    return (
      (!edgesIncludes && edges && edges.top ? this.edgeSize : 0) +
      (!edgesIncludes && edges && edges.bottom ? this.edgeSize : 0)
    );
  }

  private getCurrentRatios(): Ratios {
    const ratioRaw: string =
      this.templateController.getTemplate().attributes?.ratios?.value;

    if (!ratios[ratioRaw]) {
      return ratios.default;
    }

    return ratios[ratioRaw];
  }
}

export interface ISize {
  width: number;
  height: number;
}
